import { groupBy } from "lodash";

import { basename, extname } from "fond/fileValidation";

import { FILE_BUNDLE_EXTENSIONS } from "./acceptedFiles";

/** Extensions of formats that spread one dataset across several files. */
const SIDECAR_EXTENSIONS = FILE_BUNDLE_EXTENSIONS.filter((group) => group.length > 1).flat();

const isSidecarFormat = (file: File): boolean => {
  const extension = extname(file.name)?.toLowerCase();
  return extension !== undefined && SIDECAR_EXTENSIONS.includes(extension);
};

/**
 * Groups files by the dataset they belong to.
 *
 * Multi-file formats share a basename, so a shapefile's .shp/.dbf/.prj/.shx or a
 * MapInfo set's .tab/.dat/.id/.map form one group. Single-file formats group by
 * their full name, so `area.kml` and `area.geojson` stay apart. Two multi-file
 * datasets sharing a basename are not told apart.
 */
export const groupFilesByDataset = (files: File[]): Record<string, File[]> =>
  groupBy(files, (file) => (isSidecarFormat(file) ? basename(file.name) : file.name));

/**
 * The extensions a bundle still needs before it can be read.
 *
 * Empty when the bundle is complete, and when it matches no known format.
 */
export const findMissingSidecarExtensions = (files: File[], extensionGroups: readonly (readonly string[])[] = FILE_BUNDLE_EXTENSIONS): string[] => {
  const extensions = files.map((file) => extname(file.name)?.toLowerCase()).filter((extension): extension is string => extension !== undefined);

  const group = extensionGroups.find((groupExtensions) => extensions.some((extension) => groupExtensions.includes(extension)));

  return group ? group.filter((extension) => !extensions.includes(extension)) : [];
};
